import { toNullableText } from "../careersFormat";
import { EMPLOYMENT_TYPES, JOB_DETAIL_LIMITS } from "../consts";
import type { CareersEmploymentType, CareersJob, CareersJobInput, CareersJobShift, CareersLocationType, CareersPayUnit } from "../types";

/**
 * A job's details as the job dialog edits them: what job boards ask about a job. Numbers and text are strings while
 * typed; `null` means nothing chosen. Every detail is optional, and a blank one is saved as none.
 */
export type JobDetailsForm = {
	shift: CareersJobShift | null;
	employmentTypes: CareersEmploymentType[];
	payMin: string;
	payMax: string;
	payUnit: CareersPayUnit | null;
	city: string;
	state: string | null;
	postalCode: string;
	locationType: CareersLocationType | null;
	hoursMin: string;
	hoursMax: string;
};

/** The details that can be wrong, each showing its own problem: the typed numbers, the pay's unit and the ZIP code. */
export type JobDetailsCheckedField = "payMin" | "payMax" | "payUnit" | "hoursMin" | "hoursMax" | "postalCode";

/** What each part of the Job details section edits: the whole details form, replaced on every change. */
export type JobDetailsFieldsProps = {
	details: JobDetailsForm;
	onChange: (details: JobDetailsForm) => void;
	/** While the job saves. */
	disabled: boolean;
	/** The problem a field shows, already translated; undefined while it has none. */
	messageFor: (field: JobDetailsCheckedField) => string | undefined;
};

export type JobDetailsProblem =
	| "pay_min_invalid"
	| "pay_max_invalid"
	| "pay_max_without_min"
	| "pay_unit_required"
	| "pay_max_below_min"
	| "hours_min_invalid"
	| "hours_max_invalid"
	| "hours_max_without_min"
	| "hours_max_below_min"
	| "postal_code_invalid";

/** What each problem says: the backend's `careers.job.pay_invalid` / `hours_invalid` / `location_invalid`, split by cause. */
export const JOB_DETAILS_PROBLEM_KEY: Record<JobDetailsProblem, string> = {
	pay_min_invalid: "careers.job_dialog.problem.pay_min_invalid",
	pay_max_invalid: "careers.job_dialog.problem.pay_max_invalid",
	pay_max_without_min: "careers.job_dialog.problem.pay_max_without_min",
	pay_unit_required: "careers.job_dialog.problem.pay_unit_required",
	pay_max_below_min: "careers.job_dialog.problem.pay_max_below_min",
	// One message for both ends: it names the rule, which is the same for each.
	hours_min_invalid: "careers.job_dialog.problem.hours_invalid",
	hours_max_invalid: "careers.job_dialog.problem.hours_invalid",
	hours_max_without_min: "careers.job_dialog.problem.hours_max_without_min",
	hours_max_below_min: "careers.job_dialog.problem.hours_max_below_min",
	postal_code_invalid: "careers.job_dialog.problem.postal_code_invalid",
};

/**
 * The field each problem shows on. A problem between two fields shows on the one that has to change for it to go
 * away: the highest (pay or hours) for a range, the unit for pay without one.
 */
const JOB_DETAILS_PROBLEM_FIELD: Record<JobDetailsProblem, JobDetailsCheckedField> = {
	pay_min_invalid: "payMin",
	pay_max_invalid: "payMax",
	pay_max_without_min: "payMax",
	pay_unit_required: "payUnit",
	pay_max_below_min: "payMax",
	hours_min_invalid: "hoursMin",
	hours_max_invalid: "hoursMax",
	hours_max_without_min: "hoursMax",
	hours_max_below_min: "hoursMax",
	postal_code_invalid: "postalCode",
};

/** A new job has no details until someone fills them in. */
export const newJobDetailsForm = (): JobDetailsForm => ({
	shift: null,
	employmentTypes: [],
	payMin: "",
	payMax: "",
	payUnit: null,
	city: "",
	state: null,
	postalCode: "",
	locationType: null,
	hoursMin: "",
	hoursMax: "",
});

const numberText = (value: number | null): string => (value === null ? "" : String(value));
const nullableText = (value: string | null): string => (value === null ? "" : value);

export const jobDetailsFormFromJob = (job: CareersJob): JobDetailsForm => ({
	shift: job.shift,
	employmentTypes: job.employment_types,
	payMin: numberText(job.pay_min),
	payMax: numberText(job.pay_max),
	payUnit: job.pay_unit,
	city: nullableText(job.location_city),
	state: job.location_state,
	postalCode: nullableText(job.location_postal_code),
	locationType: job.location_type,
	hoursMin: numberText(job.hours_per_week_min),
	hoursMax: numberText(job.hours_per_week_max),
});

/** Digits, or digits grouped in threes by commas ("65,000"), with up to `decimals` places; no sign, no exponent. */
const amountPattern = (decimals: number): RegExp => new RegExp(`^(\\d+|\\d{1,3}(,\\d{3})+)(\\.\\d{1,${decimals}})?$`);
const PAY_PATTERN = amountPattern(JOB_DETAIL_LIMITS.PAY_DECIMALS);
const HOURS_PATTERN = amountPattern(JOB_DETAIL_LIMITS.HOURS_DECIMALS);
const POSTAL_CODE_PATTERN = /^\d{5}(-\d{4})?$/;

/** A typed amount as the number the API takes; null when blank; NaN when it is not an amount in [min, max]. */
const parseAmount = (text: string, pattern: RegExp, min: number, max: number): number | null => {
	const trimmed = text.trim();
	if (trimmed === "") return null;
	if (!pattern.test(trimmed)) return Number.NaN;
	const value = Number(trimmed.replaceAll(",", ""));
	return value >= min && value <= max ? value : Number.NaN;
};

const parsePay = (text: string) => parseAmount(text, PAY_PATTERN, JOB_DETAIL_LIMITS.PAY_MIN, JOB_DETAIL_LIMITS.PAY_MAX);
const parseHours = (text: string) => parseAmount(text, HOURS_PATTERN, JOB_DETAIL_LIMITS.HOURS_MIN, JOB_DETAIL_LIMITS.HOURS_MAX);
const isBelow = (high: number | null, low: number | null): boolean => high !== null && low !== null && high < low;

const payProblems = (form: JobDetailsForm): JobDetailsProblem[] => {
	const min = parsePay(form.payMin);
	const max = parsePay(form.payMax);
	const problems: JobDetailsProblem[] = [];
	if (Number.isNaN(min)) problems.push("pay_min_invalid");
	if (Number.isNaN(max)) problems.push("pay_max_invalid");
	if (min === null && max !== null) problems.push("pay_max_without_min");
	if (min !== null && form.payUnit === null) problems.push("pay_unit_required");
	// NaN compares false, so a range is only checked once both ends are amounts.
	if (isBelow(max, min)) problems.push("pay_max_below_min");
	return problems;
};

const hoursProblems = (form: JobDetailsForm): JobDetailsProblem[] => {
	const min = parseHours(form.hoursMin);
	const max = parseHours(form.hoursMax);
	const problems: JobDetailsProblem[] = [];
	if (Number.isNaN(min)) problems.push("hours_min_invalid");
	if (Number.isNaN(max)) problems.push("hours_max_invalid");
	if (min === null && max !== null) problems.push("hours_max_without_min");
	if (isBelow(max, min)) problems.push("hours_max_below_min");
	return problems;
};

const postalCodeProblems = (form: JobDetailsForm): JobDetailsProblem[] => {
	const postalCode = form.postalCode.trim();
	return postalCode === "" || POSTAL_CODE_PATTERN.test(postalCode) ? [] : ["postal_code_invalid"];
};

/**
 * Why these details would be refused — the backend's rules, checked up front so Save says what to fix. Nothing filled
 * in is fine; pay needs a minimum above $0 with its unit, and a maximum no lower than it; hours a week are 1-168 with
 * one decimal, the highest no lower than the lowest; a ZIP code is 5 digits or ZIP+4. A highest without a lowest says
 * nothing a reader can trust, so it is refused for pay and hours alike. Empty list = savable.
 */
export const jobDetailsProblems = (form: JobDetailsForm): JobDetailsProblem[] => [...payProblems(form), ...hoursProblems(form), ...postalCodeProblems(form)];

/** The problem each field shows: the first of its own, so "500" hours says it is out of range before anything else. */
export const jobDetailsFieldProblems = (form: JobDetailsForm): Partial<Record<JobDetailsCheckedField, JobDetailsProblem>> => {
	const byField: Partial<Record<JobDetailsCheckedField, JobDetailsProblem>> = {};
	for (const problem of jobDetailsProblems(form)) {
		const field = JOB_DETAILS_PROBLEM_FIELD[problem];
		if (byField[field] === undefined) byField[field] = problem;
	}
	return byField;
};

export type JobDetailsInput = Pick<
	CareersJobInput,
	| "shift"
	| "employment_types"
	| "pay_min"
	| "pay_max"
	| "pay_unit"
	| "location_city"
	| "location_state"
	| "location_postal_code"
	| "location_type"
	| "hours_per_week_min"
	| "hours_per_week_max"
>;

/**
 * The request's job details. Only call once `jobDetailsProblems` is empty — it throws otherwise. Without a minimum
 * there is no pay at all, so a unit chosen on its own is dropped with it (the backend refuses a unit alone).
 */
export const jobDetailsInput = (form: JobDetailsForm): JobDetailsInput => {
	const problems = jobDetailsProblems(form);
	if (problems.length > 0) throw new Error(`jobDetailsInput called with problems: ${problems.join(", ")}`);
	const payMin = parsePay(form.payMin);
	return {
		shift: form.shift,
		// In the dialog's order, so the same choice always sends the same body.
		employment_types: EMPLOYMENT_TYPES.filter((type) => form.employmentTypes.includes(type)),
		pay_min: payMin,
		pay_max: parsePay(form.payMax),
		pay_unit: payMin === null ? null : form.payUnit,
		location_city: toNullableText(form.city),
		location_state: form.state,
		location_postal_code: toNullableText(form.postalCode),
		location_type: form.locationType,
		hours_per_week_min: parseHours(form.hoursMin),
		hours_per_week_max: parseHours(form.hoursMax),
	};
};
